import {CustomElement} from "./CustomElement";

let idCounter = 0xbeef;

/**
 * NOTE: This is based off the 'named insertion point' proposal:
 * https://github.com/w3c/webcomponents/blob/gh-pages/proposals/Proposal-for-changes-to-manage-Shadow-DOM-content-distribution.md
 */
export class ShadowElement extends CustomElement {
	protected _transcludedContent: Node[] = [];
	private _uid = idCounter++;

	protected _transclude() {
		// Get all children that may have already been transcluded by this
		// element
		const currentlyTranscluded: Node[] = Array.prototype.slice.apply(
				this.querySelectorAll(`[transcluded='${this._uid}']`));

		// Make sure we also include non-element types
		for (let i = 0; i < this.childNodes.length; i++) {
			const child = this.childNodes[i];
			if ((<any> child)._transcluded === this._uid) {
				currentlyTranscluded.push(child);
			}
		}


		// If we found something already transcluded then we haven't been
		// re-rendered, otherwise recapture...
		if (currentlyTranscluded.length > 0) {
			this._transcludedContent = currentlyTranscluded;
		} else {
			this._transcludedContent = [];

			for (let i = 0; i < this.childNodes.length; i++) {
				const child = this.childNodes[i];

				if (child.nodeType === Node.ELEMENT_NODE) {
					(<Element> child).setAttribute("transcluded", this._uid.toString());
				} else {
					(<any> child)._transcluded = this._uid;
				}

				this._transcludedContent.push(child);
			}
		}
	}

	protected _renderShadowDOM(template: HTMLElement) {
		// assert template.tagName === 'template'

		// get all the content slots
		const slots: { [slot: string]: Element } = {};
		const fragments: { [slot: string]: DocumentFragment } = {};
		let defaultSlot: Element = null;
		let defaultFragment: DocumentFragment = null;
		const contentEl = template.querySelectorAll("content");
		if (contentEl.length === 0) return; // element does not transclude

		for (let i = 0; i < contentEl.length; i++) {
			const el = contentEl[i];
			if (el.hasAttribute("slot")) {
				const slotName = el.getAttribute("slot");
				if (slotName in slots) throw new Error(
						`Duplicate slot '${slotName}' for '${this.tagName} element.`);

				slots[slotName] = el;
				fragments[slotName] = document.createDocumentFragment();
			} else {
				// default slot
				if (defaultSlot !== null) throw new Error(
						`Duplicate default slot for '${this.tagName}'.`);

				defaultSlot = el;
				defaultFragment = document.createDocumentFragment();
			}
		}

		// place the transcluded elements
		this._transcludedContent.forEach((el) => {
			if (el.nodeType === Node.ELEMENT_NODE &&
					(<Element> el).hasAttribute("content-slot")) {
				const slotName = (<Element> el).getAttribute("content-slot");
				if (!(slotName in slots)) throw new Error(
						`Slot '${slotName}' not found in '${this.tagName}'.`);

				fragments[slotName].appendChild(el);
			} else if (defaultFragment !== null) {
				defaultFragment.appendChild(el);
			}
		});

		// replace the content elements with their fragments
		Object.getOwnPropertyNames(slots).forEach((slotName) => {
			slots[slotName].parentNode.replaceChild(fragments[slotName],
					slots[slotName]);
		});
		if (defaultSlot) defaultSlot.parentNode.replaceChild(
				defaultFragment, defaultSlot);

		// insert the template as the DOM
		while (this.firstChild) this.removeChild(this.firstChild);
		while (template.firstChild) this.appendChild(template.firstChild);
	}
}
